import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {
	IPredictionApiProvider,
	IPrediction,
} from "data/providers/api/predictions.api.provider";
import {findIndex} from "lodash";
import {Empty} from "data/types/generics";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {AxiosError} from "axios";
import {IAxiosApiError} from "data/types/api";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IJSONProvider, ITips} from "data/providers/json/json.provider";
import type {IUserStore} from "data/stores/user/user.store";
import {GAME_ZONE_URL} from "data/constants";
import {getUri} from "views/components/route";
import {ModalType, TournamentStatus} from "data/enums";
import type {ISharingApiProvider} from "data/providers/api/sharing.api.provider";
import i18n from "i18next";

export interface IPredictionsStore {
	get getIsLoading(): boolean;
	get list(): IPrediction[];
	get tips(): ITips | null;
	get isChanged(): boolean;
	get isSaved(): boolean;
	get predictionsForSend(): IPrediction[];
	get imagePathForSharing(): string | null;

	getPredictionByTournamentId(id: number): Empty<IPrediction>;
	updatePredictions(newPrediction: IPrediction): void;
	fetchPredictionsAnswers(roundId: number): Promise<void>;
	savePredictionsAnswers(): void;
	fetchPredictionsTips(selectedRound: number): Promise<void>;
	savePredictionsTemporaryAnswers(): void;
	fetchAutofill(): void;
	fetchSharingUrl(): Promise<void>;
	clearSelectedPrediction(): void;
	setPredictionsTemporaryAnswers(): void;
}

@injectable()
export class PredictionsStore implements IPredictionsStore {
	@observable private _isLoading: boolean = false;
	@observable private _isLoadingSave: boolean = false;
	@observable private _isLoadingTips: boolean = false;
	@observable private _isSaved: boolean = false;
	@observable private _isChanged: boolean = false;
	@observable private _imagePathForSharing: string | null = null;
	@observable readonly _temporaryPicksKey: string = "temporary_picks";
	@observable private _allPredictionSaved: boolean = false;

	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.PredictionApiProvider) private _predictionProvider: IPredictionApiProvider,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.SharingApiProvider) private _sharingApiProvider: ISharingApiProvider
	) {
		makeAutoObservable(this);
	}

	@observable private _list: IPrediction[] = [];
	@observable private _tips: ITips | null = null;

	get list() {
		return this._list;
	}

	get predictionsForSend() {
		const tournaments = this._roundsStore.currentSelectedRound?.tournaments || [];

		// Send prediction where tournament is Scheduled
		return this.list
			.filter((item) => {
				return tournaments.find(
					(tournament) =>
						item.tournament === tournament.id &&
						tournament.status === TournamentStatus.Scheduled
				);
			})
			.map((prediction) => ({
				tournament: prediction.tournament,
				squad: prediction.squad,
				margin: prediction.margin,
			}));
	}

	get tips() {
		return this._tips;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get isChanged(): boolean {
		return this._isChanged;
	}

	get isSaved(): boolean {
		return this._isSaved;
	}

	get imagePathForSharing(): string | null {
		return this._imagePathForSharing;
	}

	public getPredictionByTournamentId(id: number): Empty<IPrediction> {
		return this._list.find((prediction) => prediction.tournament === id);
	}

	protected onError(e: unknown) {
		const error = e as AxiosError<IAxiosApiError, unknown>;
		this._modalsStore.showAxiosError(error);
	}

	@action
	public updatePredictions(newPrediction: IPrediction): void {
		this._isChanged = true;
		this._isSaved = false;

		const foundIndex = findIndex(
			this._list,
			(prediction) => prediction.tournament === newPrediction.tournament
		);
		if (foundIndex !== -1) {
			this._list[foundIndex] = newPrediction;
		} else {
			this._list = this._list.concat([newPrediction]);
		}
	}

	@action
	public async fetchPredictionsAnswers(roundId: number): Promise<void> {
		if (!roundId || this._isLoading) {
			return;
		}

		try {
			this._isLoading = true;
			this._isChanged = false;
			const {data} = await this._predictionProvider.answers(roundId);
			runInAction(() => {
				this._list = data.success.predictions;
			});
		} catch (e) {
			this._isLoading = false;
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoading = false;
		}
	}

	public setPredictionsTemporaryAnswers(): void {
		localStorage.setItem(this._temporaryPicksKey, JSON.stringify(this.predictionsForSend));
	}

	public clearSelectedPrediction(): void {
		const tournaments = this._roundsStore.currentRound?.tournaments || [];

		this._list = this._list.filter((item) => {
			return tournaments.find(
				(tournament) =>
					item.tournament === tournament.id &&
					tournament.status !== TournamentStatus.Scheduled
			);
		});
	}

	@action
	public savePredictionsTemporaryAnswers(): void {
		const predictions = JSON.parse(localStorage.getItem(this._temporaryPicksKey) || "null") as
			| IPrediction[]
			| null;

		if (
			this._userStore.isAuthorized &&
			this._userStore.profile?.terms &&
			this._roundsStore.currentSelectedRound &&
			predictions
		) {
			void this.sendPredictionsAnswers(predictions).finally(() => {
				localStorage.removeItem(this._temporaryPicksKey);
			});
		}
	}

	@action
	public savePredictionsAnswers(): void {
		if (!this._userStore.isAuthorized) {
			this.setPredictionsTemporaryAnswers();
			window.location.href = `${GAME_ZONE_URL}${getUri(window.location.pathname)}`;
			return;
		}

		void this.sendPredictionsAnswers(this.predictionsForSend);
	}

	private async sendPredictionsAnswers(predictions: IPrediction[]): Promise<void> {
		if (this._isLoadingSave) {
			return;
		}
		try {
			this._isLoadingSave = true;
			this._isChanged = false;
			this._isSaved = true;
			const {data} = await this._predictionProvider
				.save({
					predictions: predictions,
					round: this._roundsStore.currentSelectedRound?.id || 0,
				})
				.finally(() => {
					if (
						this._roundsStore.tournamentsBySelectedRound.length === predictions.length
					) {
						this._modalsStore.showModal(ModalType.PICKSSAVED);
					}
				});
			console.log(data);
			// runInAction(() => {
			// 	this._list = data.success.predictions;
			// });
		} catch (e) {
			this._isLoadingSave = false;
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoadingSave = false;
		}
	}

	@action
	public async fetchPredictionsTips(selectedRound: number): Promise<void> {
		if (this._isLoadingTips) {
			return;
		}
		try {
			this._isLoadingTips = true;
			const {data} = await this._jsonProvider.tips(selectedRound);
			this._tips = data;
		} catch (e) {
			this._isLoadingTips = false;
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoadingTips = false;
		}
	}

	@action
	public async fetchAutofill(): Promise<void> {
		if (!this._roundsStore.currentSelectedRound || this._isLoading) {
			return;
		}

		try {
			this._isLoading = true;
			this._isChanged = true;
			this._isSaved = false;
			const {data} = await this._predictionProvider.autofill({
				round: this._roundsStore.currentSelectedRound.id,
				predictions: this.predictionsForSend,
			});
			runInAction(() => {
				this._list = this._list.concat(data.success.predictions);
			});
		} catch (e) {
			this._isLoading = false;
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoading = false;
		}
	}

	@action
	public async fetchSharingUrl(): Promise<void> {
		const language = i18n.language || "en";

		try {
			const {data} = await this._sharingApiProvider.getPredictionImage({
				roundId: this._roundsStore.currentSelectedRound?.id || 0,
				locale: language,
			});

			this._imagePathForSharing = data.success.imagePath;
		} catch (e) {
			this.onError(e);
			return Promise.reject(e);
		}
	}
}
